import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'

type Status = { text: string; tone: 'ok' | 'warn' | 'err' } | null

const FORMATS = ['code_128', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_39', 'itf', 'codabar', 'qr_code']

/** Overlay de câmera que lê código de barras / QR ao vivo.
 *  Usa o detector NATIVO do navegador (BarcodeDetector) quando disponível
 *  — mais confiável no celular — e cai para o ZXing quando não houver. */
export function CameraScan({ onScan, onClose, hint, status }: {
  onScan: (text: string) => void
  onClose: () => void
  hint?: string
  status?: Status
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const zxingRef = useRef<{ stop: () => void } | null>(null)
  const lastRef = useRef<{ code: string; at: number }>({ code: '', at: 0 })
  const audioRef = useRef<AudioContext | null>(null)
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan
  const [error, setError] = useState<string | null>(null)
  const [read, setRead] = useState<string | null>(null)
  const [engine, setEngine] = useState<string>('')

  function beep() {
    try {
      const ctx = audioRef.current
      if (!ctx) return
      if (ctx.state === 'suspended') ctx.resume()
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.type = 'square'
      o.frequency.value = 880
      g.gain.value = 0.18
      o.connect(g); g.connect(ctx.destination)
      o.start()
      o.stop(ctx.currentTime + 0.12)
    } catch { /* ignore */ }
  }

  function handle(raw: string) {
    const text = raw.trim()
    if (!text) return
    const now = Date.now()
    setRead(text)
    if (text === lastRef.current.code && now - lastRef.current.at < 1500) return
    lastRef.current = { code: text, at: now }
    beep()
    try { navigator.vibrate?.(120) } catch { /* ignore */ }
    onScanRef.current(text)
  }

  useEffect(() => {
    let cancelled = false
    try {
      const AC = window.AudioContext || (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (AC) { audioRef.current = new AC(); audioRef.current.resume() }
    } catch { /* ignore */ }
    ;(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        })
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }
        streamRef.current = stream
        const video = videoRef.current!
        video.srcObject = stream
        await video.play().catch(() => { /* ignore */ })

        const Detector = (window as { BarcodeDetector?: any }).BarcodeDetector
        if (Detector) {
          setEngine('nativo')
          let supported: string[] = []
          try { supported = await Detector.getSupportedFormats() } catch { /* ignore */ }
          const formats = FORMATS.filter(f => !supported.length || supported.includes(f))
          const detector = new Detector(formats.length ? { formats } : undefined)
          const tick = async () => {
            if (cancelled) return
            try {
              const codes = await detector.detect(video)
              if (codes && codes.length) handle(codes[0].rawValue)
            } catch { /* frame sem código */ }
            timerRef.current = setTimeout(tick, 120)
          }
          tick()
        } else {
          setEngine('zxing')
          const [{ BrowserMultiFormatReader }, { DecodeHintType, BarcodeFormat }] = await Promise.all([
            import('@zxing/browser'), import('@zxing/library'),
          ])
          const hints = new Map()
          hints.set(DecodeHintType.TRY_HARDER, true)
          hints.set(DecodeHintType.POSSIBLE_FORMATS, [
            BarcodeFormat.CODE_128, BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
            BarcodeFormat.UPC_A, BarcodeFormat.CODE_39, BarcodeFormat.ITF, BarcodeFormat.QR_CODE,
          ])
          const reader = new BrowserMultiFormatReader(hints)
          const controls = await reader.decodeFromVideoElement(
            video,
            (result: import('@zxing/library').Result | undefined) => { if (result) handle(result.getText()) },
          )
          if (cancelled) controls.stop()
          else zxingRef.current = controls
        }
      } catch (e) {
        if (!cancelled) {
          const name = (e as { name?: string })?.name
          setError(name === 'NotAllowedError'
            ? 'Permissão de câmera negada. Libere a câmera nas configurações do navegador.'
            : 'Não consegui acessar a câmera (use HTTPS e permita o acesso).')
        }
      }
    })()
    return () => {
      cancelled = true
      if (timerRef.current) clearTimeout(timerRef.current)
      zxingRef.current?.stop()
      streamRef.current?.getTracks().forEach(t => t.stop())
      audioRef.current?.close().catch(() => { /* ignore */ })
    }
  }, [])

  const toneCls = status?.tone === 'ok' ? 'bg-green-600' : status?.tone === 'warn' ? 'bg-amber-500' : 'bg-red-600'

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-sm font-medium">{hint ?? 'Aponte para o código'}</span>
        <button onClick={onClose} className="rounded-full bg-white/10 p-2 active:bg-white/20" aria-label="Fechar">
          <X className="h-5 w-5" />
        </button>
      </div>

      {status && (
        <div className={`break-words px-4 py-3 text-center text-base font-semibold text-white ${toneCls}`}>{status.text}</div>
      )}

      <div className="relative flex-1">
        {error ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-red-300">{error}</div>
        ) : (
          <>
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline autoPlay />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="h-28 w-4/5 rounded-xl border-2 border-green-400/90 shadow-[0_0_0_2000px_rgba(0,0,0,0.35)]" />
            </div>
          </>
        )}
      </div>

      <div className="bg-black/80 px-4 py-3 text-center">
        {read
          ? <p className="break-all font-mono text-sm text-green-400">Lido: {read}</p>
          : <p className="text-xs text-white/60">Encaixe o código na moldura, ~15–20 cm, boa luz.</p>}
        {engine && <p className="mt-0.5 text-[10px] text-white/40">motor: {engine}</p>}
      </div>
    </div>
  )
}
